import { createAsyncThunk } from '@reduxjs/toolkit'
import { FTL_WEB_APP, LOCAL_STORAGE } from 'app/commons/constants'
import { crossStorageUtils } from 'app/commons/helpers/crossStorage'
import { hideLoading, showLoading } from 'app/commons/helpers/loadingUtils'
import { objectToFormData } from 'app/commons/helpers/utils'
import authApi from 'app/services/authApi'
import i18n from 'i18next'

import { toast } from 'react-toastify'

import {
  IRequestAuth,
  IRequestInfoSignUp,
  IRequestNewPwd,
  IRequestSignIn,
  IRequestSwitchAccount,
  IRequestVerify,
  ISwitchAccount,
} from '../../interface/IAuth'
import { messageErrorAction } from '../messageError/messageErrorSlice'

export const postSignIn = createAsyncThunk('auth/signIn', async (payload: IRequestSignIn, thunkAPI) => {
  try {
    const { info, cb, xRecaptchaToken } = payload
    const result = await authApi.signIn(info, xRecaptchaToken)
    if (result?.error) {
      return thunkAPI.dispatch(
        messageErrorAction.setMessageErrors({
          messageError: result?.error,
          isOpen: true,
          optionals: {
            isWaring: true,
          },
        })
      )
    }
    window.onbeforeunload = null
    const { object, access_token: token } = result
    const employId = object.last_login_employ_id
    cb(result.object)
    crossStorageUtils.setAccessToken(token)
    postSwitchAccount({ employId, token })
    return result
  } catch (error) {
    console.debug(error)
  }
})

export const postSwitchAccount = async (payload: ISwitchAccount) => {
  try {
    let { token } = payload
    if (payload?.employId) {
      const result = await authApi.switchAccount(payload.employId)
      token = result.access_token
      crossStorageUtils.setAccessToken(token)
    }
  } catch (error) {
    console.debug(error)
  }
}

export const postSignUp = async (fn: IRequestInfoSignUp) => {
  try {
    const result = await authApi.signUp(fn.dataInfo, fn.xRecaptchaToken)
    if (result?.error) {
      fn.onSignUpError(result?.error)
    } else if (result.error_code) {
      fn.onSignUpError(result?.message)
    } else {
      const resultToken = await authApi.convertToken(result.access_token, '')
      const accessToken = resultToken?.access_token
      window.localStorage.setItem(LOCAL_STORAGE.JUST_CREATED_ACCOUNT, 'true')
      crossStorageUtils.setAccessToken(accessToken)
      fn.onSignUpSuccess(result?.object)
    }
  } catch (error) {
    console.debug(error)
  }
}

export const handleCheckVerificationCode = createAsyncThunk(
  'auth/CheckVerificationCode',
  async (verity: IRequestVerify) => {
    try {
      const { verificationCode, callback, xRecaptchaToken } = verity
      const verifyResponse = await authApi.checkVerificationCode(
        {
          sms_token: verificationCode,
        },
        xRecaptchaToken
      )
      if (verifyResponse.error && verifyResponse.status === 400) {
        toast.error(verifyResponse.error)
      } else if (verifyResponse.error && verifyResponse.status === 405) {
        toast.error(i18n.t('common.verifyCode.verifyFailed') as string)
      } else {
        callback()
        toast.success(i18n.t('common.verifyCode.verifyCode') as string)
      }
    } catch (error: any) {
      if (error.result && error.result.error) {
        toast.error(error.result.error)
      }
      console.debug(error)
    }
  }
)

export const handleGetAuth = createAsyncThunk('auth/handleGetAuth', async (payload: IRequestAuth) => {
  try {
    const { accountId, cb } = payload
    const { data: accountResult } = await authApi.getAccountCustomer()
    const token = localStorage.getItem(LOCAL_STORAGE.ACCESS_TOKEN) || ''
    const queryParam = {
      'only[]': [
        'allow_post_payment',
        'ltl_booking_start_time',
        'ltl_booking_end_time',
        'ltl_maximum_pickup_time_days',
        'default_note',
        'hide_area_selector',
      ],
    }
    const language = localStorage.getItem(LOCAL_STORAGE.DEFAULT_LANGUAGE)
    const configResult = await authApi.getCustomerConfig(accountId, queryParam, language)
    let dataAccount = accountResult.object
    localStorage.setItem(LOCAL_STORAGE.ACCOUNT_INFO, JSON.stringify(dataAccount))
    dataAccount = { ...dataAccount, ...configResult }
    // sync data FTL to LTL
    window.fcWidget.close()
    cb?.()
    return { accounts: dataAccount, access_token: token, customerFtlConfig: configResult }
  } catch (error) {
    console.debug('handleGetAuth ERROR', error)
  }
})

export const handleSubmitNewPwd = async (data: IRequestNewPwd) => {
  try {
    const { password, currentPassword, cb } = data
    const queryParam = {
      password,
      current_password: currentPassword,
    }
    const result = await authApi.updateCustomerMe(queryParam, null)
    if (result?.object?.id) {
      cb && cb()
    } else {
      toast.error(result?.error)
    }
  } catch (error) {
    console.log('error', error)
  }
}

export const handleSwitchAccount = async (data: IRequestSwitchAccount) => {
  try {
    const { id, callback } = data
    const result = await authApi.switchAccount(id)
    if (result?.access_token) crossStorageUtils.setAccessToken(result?.access_token)
    callback()
  } catch (error) {
    console.log('handleSwitchAccount ERROR', error)
  } finally {
    hideLoading()
  }
}

export const logout = async () => {
  try {
    const result = await authApi.deleteLogout()
    if (result.status === 204) {
      localStorage.removeItem(LOCAL_STORAGE.ACCESS_TOKEN)
      window.onbeforeunload = null
      window.location.href = `${FTL_WEB_APP}/?log_out_success=true`
    } else {
      toast.error(i18n.t('common.dlvrApiError.logoutUnsuccessful') as string)
    }
  } catch (error: any) {
    toast.error(error?.response)
    console.log('error', error)
  }
}

export const handleGetWallet = createAsyncThunk('auth/handleGetWallet', async (callback?: (data: object) => void) => {
  try {
    const countryCode = localStorage.getItem(LOCAL_STORAGE.DEFAULT_COUNTRY) || 'id'
    const result = await authApi.getWallet(countryCode)
    callback && callback(result.object)
    return result.object
  } catch (error) {
    hideLoading()
  }
})

export const editProfile = createAsyncThunk('auth/editProfile', async (data: any) => {
  try {
    showLoading()
    const { payload, cb } = data
    const { country_code, avatar, last_name, first_name, password, new_password, business_name, is_update_avatar } =
      payload
    const formData = {
      ...(avatar && { avatar }),
      ...(country_code && { country_code }),
      ...(last_name && { last_name }),
      ...(first_name && { first_name }),
      ...(new_password && { password: new_password }),
      ...(password && { current_password: password }),
      ...(business_name && { business_name: business_name }),
      ...(is_update_avatar && { is_update_avatar }),
    }
    const body = objectToFormData(formData)
    const result = await authApi.getCustomer(body)
    cb && cb()
    return { login: result, ...(is_update_avatar && { is_update_avatar }) }
  } catch (ex: any) {
    console.log(ex)
  } finally {
    hideLoading()
  }
})

export const handleLoginSocial = createAsyncThunk(
  'auth/handleLoginSocial',
  async (data: { params: any; callback: (data?: any) => void; xRecaptchaToken: string | null }) => {
    try {
      const { params, callback, xRecaptchaToken } = data
      const result = await authApi.loginSocialAccount(params, xRecaptchaToken)
      if (result?.access_token) crossStorageUtils.setAccessToken(result?.access_token)
      callback(result || {})
    } catch (error) {
      hideLoading()
    }
  }
)
